import React from "react";
import { Plane, Briefcase, CarTaxiFront, Package, TrainFront, Map, Stethoscope, GraduationCap, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  title: "Airport Transfers",
  description: "Private pick-up and drop-off at SFO, OAK, and SJC. Flight tracked in real time - your driver adjusts before you land.",
  icon: Plane,
  className: "lg:col-span-2 lg:row-span-2",
  link: "/HomePage"
}, {
  title: "Executive Travel",
  description: "Quiet, high-spec vehicles for corporate leaders who value time, privacy, and a seamless ride between meetings.",
  icon: Briefcase,
  className: "lg:col-span-1 lg:row-span-2",
  link: "/CorporatePage"
}, {
  title: "Hourly Chauffeur",
  description: "A dedicated professional at your disposal - by the hour, on your terms, wherever the day takes you.",
  icon: CarTaxiFront,
  className: "lg:col-span-1 lg:row-span-1",
  link: "/HomePage"
}, {
  title: "Corporate Accounts",
  description: "Streamlined billing, priority dispatch, and dedicated account management for Silicon Valley firms and frequent travelers.",
  icon: Package,
  className: "lg:col-span-1 lg:row-span-1",
  link: "/CorporatePage"
}, {
  title: "Private Events",
  description: "Bespoke transportation for weddings, galas, and special celebrations across Northern California.",
  icon: TrainFront,
  className: "lg:col-span-2 lg:row-span-1",
  link: "/HomePage"
}, {
  title: "Wine Country Tours",
  description: "Napa Valley and Sonoma, at your pace. A dedicated chauffeur, premium vehicle, and no detail overlooked.",
  icon: Map,
  className: "lg:col-span-2 lg:row-span-1",
  link: "/HomePage"
}, {
  title: "Medical Concierge",
  description: "Discreet and comfortable transport for private medical appointments and specialized care facilities.",
  icon: Stethoscope,
  className: "lg:col-span-2 lg:row-span-1",
  link: "/HomePage"
}, {
  title: "Long-Distance",
  description: "The premier alternative to short-haul flights. Intercity travel across California in total luxury.",
  icon: GraduationCap,
  className: "lg:col-span-2 lg:row-span-1",
  link: "/HomePage"
}];
export default function TheCityRideSpectrum() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="7404" id="bespoke-transportation-solutions" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-primary/5 pointer-events-none" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="mb-16 max-w-3xl">
          <h2 className="text-4xl md:text-5xl font-serif font-bold tracking-tight mb-6">
            Ground Transportation, <span className="text-primary">Redefined.</span>
          </h2>
          <p className="text-xl text-muted-foreground font-sans">
            From private airport transfers to full-day executive itineraries, AAP Luxury Transfers operates across the San Francisco Bay Area with one standard: yours. Available 24/7 for individuals, corporate accounts, and special occasions.
          </p>
        </div>

        <div ref={ref} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {services.map((service, i) => <Link key={i} to={service.link} className={`group block h-full ${service.className}`} data-index={i}>
              <Card className="h-full bg-card border-border/40 hover:border-primary/60 transition-all duration-500 hover:shadow-2xl hover:shadow-primary/10 overflow-hidden relative rounded-sm">
                <CardHeader className="relative z-10">
                  <div className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-none bg-secondary/80 group-hover:bg-primary/20 transition-colors duration-500 border border-primary/10">
                    <service.icon className="w-6 h-6 text-primary transition-transform duration-700 group-hover:scale-110 group-hover:rotate-2" strokeWidth={1.5} />
                  </div>
                  <CardTitle className="text-xl md:text-2xl font-serif group-hover:text-primary transition-colors duration-500">
                    {service.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="relative z-10 flex flex-col justify-between h-full pb-16">
                  <CardDescription className="text-base text-muted-foreground group-hover:text-foreground/90 transition-colors duration-500 leading-relaxed font-sans">
                    {service.description}
                  </CardDescription>
                  
                  <div className="absolute bottom-6 right-6 opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500">
                    <Button size="icon" variant="ghost" className="rounded-none border border-primary/50 hover:bg-primary hover:text-primary-foreground shadow-lg shadow-primary/20">
                      <ArrowRight className="w-5 h-5" />
                    </Button>
                  </div>
                </CardContent>
                
                {}
                <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-primary/5 rounded-full blur-3xl group-hover:bg-primary/15 transition-all duration-700" />
              </Card>
            </Link>)}
        </div>

        <div className="mt-12 text-center md:text-left">
          <Link to="#">
            <Button size="lg" className="text-base px-10 font-medium rounded-none border border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300 shadow-xl shadow-primary/5">
              Secure Your Chauffeur
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}